import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import { api } from "@/lib/api";
import { startUpdateWatcher } from "@/lib/appUpdate";
import { Logo } from "@/components/shared/Logo";

import PublicLayout from "@/components/public/PublicLayout";
import Home from "@/pages/public/Home";
import Services from "@/pages/public/Services";
import About from "@/pages/public/About";
import WhyCastleCorp from "@/pages/public/WhyCastleCorp";
import ServiceAreas from "@/pages/public/ServiceAreas";
import Quote from "@/pages/public/Quote";
import Book from "@/pages/public/Book";
import JobLink from "@/pages/public/JobLink";
import Contact from "@/pages/public/Contact";
import { Pricing, FAQ, Careers, Privacy, Terms } from "@/pages/public/StaticPages";

import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import JoinCrew from "@/pages/auth/JoinCrew";

import PortalLayout from "@/components/portal/PortalLayout";
import PortalOverview from "@/pages/portal/PortalOverview";
import PortalQuotes from "@/pages/portal/PortalQuotes";
import PortalJobs from "@/pages/portal/PortalJobs";
import PortalInvoices from "@/pages/portal/PortalInvoices";
import PortalMessages from "@/pages/portal/PortalMessages";
import PortalDocuments from "@/pages/portal/PortalDocuments";
import { PaymentSuccess, PaymentCancel } from "@/pages/portal/PaymentResult";

import OpsLayout from "@/components/ops/OpsLayout";
import OpsDashboard from "@/pages/ops/OpsDashboard";
import QuotesInbox from "@/pages/ops/QuotesInbox";
import QuoteGenerator from "@/pages/ops/QuoteGenerator";
import CreateJob from "@/pages/ops/CreateJob";
import CRM from "@/pages/ops/CRM";
import Missions from "@/pages/ops/Missions";
import OpsCalendar from "@/pages/ops/OpsCalendar";
import Dispatch from "@/pages/ops/Dispatch";
import Resources from "@/pages/ops/Resources";
import Customers from "@/pages/ops/Customers";
import SupportInbox from "@/pages/ops/SupportInbox";
import Invoices from "@/pages/ops/Invoices";
import PricingEngine from "@/pages/ops/PricingEngine";
import Finance from "@/pages/ops/Finance";
import Analytics from "@/pages/ops/Analytics";
import AIOps from "@/pages/ops/AIOps";
import OpsSettings from "@/pages/ops/OpsSettings";
import CrewPortal from "@/pages/crew/CrewPortal";
import DispatchDashboard from "@/pages/ops/DispatchDashboard";

const Loading = () => (
  <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
    <Logo size={44} className="text-foreground animate-pulse" />
    <p className="overline-cc">Initializing</p>
  </div>
);

function AuthCallback() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const params = new URLSearchParams(location.hash.replace("#", ""));
    const sessionId = params.get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        setUser(data);
        window.history.replaceState(null, "", window.location.pathname);
        navigate(data.role === "admin" ? "/ops" : "/portal", { replace: true, state: { user: data } });
      } catch {
        navigate("/login", { replace: true });
      }
    })();
  }, []); // eslint-disable-line

  return <Loading />;
}

function ProtectedRoute({ children, role }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  if (role === "ops" && !["admin", "dispatcher"].includes(user.role)) return <Navigate to={roleHome(user.role)} replace />;
  if (role === "admin" && user.role !== "admin") return <Navigate to={roleHome(user.role)} replace />;
  if (role === "crew" && user.role !== "crew") return <Navigate to={roleHome(user.role)} replace />;
  if (role === "customer" && user.role !== "customer") return <Navigate to={roleHome(user.role)} replace />;
  return children;
}

// Owner-only surfaces inside CastleOps — a dispatcher is bounced back to their deck.
function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user && user !== false && user.role !== "admin") return <Navigate to="/ops" replace />;
  return children;
}

// The /ops index shows the owner's executive dashboard or the dispatcher deck.
function OpsHome() {
  const { user } = useAuth();
  return user?.role === "dispatcher" ? <DispatchDashboard /> : <OpsDashboard />;
}

function roleHome(role) {
  return role === "crew" ? "/crew" : role === "customer" ? "/portal" : "/ops";
}

// PWA standalone cold-open: when the app is launched from the home-screen icon
// and the user is already authenticated, land them in their portal instead of
// the marketing homepage.
function StandaloneRedirector({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const isStandalone = typeof window !== "undefined" && (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator?.standalone === true
  );
  if (isStandalone && location.pathname === "/" && user && user !== false) {
    return <Navigate to={roleHome(user.role)} replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation(); // read hash from here, not window.location.hash (not reactive)
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route element={<StandaloneRedirector><PublicLayout /></StandaloneRedirector>}>
        <Route path="/" element={<Home />} />
        <Route path="/services" element={<Services />} />
        <Route path="/about" element={<About />} />
        <Route path="/why-castlecorp" element={<WhyCastleCorp />} />
        <Route path="/service-areas" element={<ServiceAreas />} />
        <Route path="/quote" element={<Quote />} />
        <Route path="/book" element={<Book />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
      </Route>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/join" element={<JoinCrew />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/job/:token" element={<JobLink />} />
      <Route path="/portal" element={<ProtectedRoute role="customer"><PortalLayout /></ProtectedRoute>}>
        <Route index element={<PortalOverview />} />
        <Route path="quotes" element={<PortalQuotes />} />
        <Route path="jobs" element={<PortalJobs />} />
        <Route path="invoices" element={<PortalInvoices />} />
        <Route path="messages" element={<PortalMessages />} />
        <Route path="documents" element={<PortalDocuments />} />
      </Route>
      <Route path="/crew" element={<ProtectedRoute role="crew"><CrewPortal /></ProtectedRoute>} />
      <Route path="/ops" element={<ProtectedRoute role="ops"><OpsLayout /></ProtectedRoute>}>
        <Route index element={<OpsHome />} />
        <Route path="quotes" element={<QuotesInbox />} />
        <Route path="quote-generator" element={<QuoteGenerator />} />
        <Route path="create-job" element={<CreateJob />} />
        <Route path="crm" element={<CRM />} />
        <Route path="missions" element={<Missions />} />
        <Route path="calendar" element={<OpsCalendar />} />
        <Route path="dispatch" element={<Dispatch />} />
        <Route path="resources" element={<Resources />} />
        <Route path="customers" element={<Customers />} />
        <Route path="support" element={<SupportInbox />} />
        <Route path="invoices" element={<AdminOnly><Invoices /></AdminOnly>} />
        <Route path="pricing" element={<AdminOnly><PricingEngine /></AdminOnly>} />
        <Route path="finance" element={<AdminOnly><Finance /></AdminOnly>} />
        <Route path="analytics" element={<AdminOnly><Analytics /></AdminOnly>} />
        <Route path="ai" element={<AdminOnly><AIOps /></AdminOnly>} />
        <Route path="settings" element={<AdminOnly><OpsSettings /></AdminOnly>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => { startUpdateWatcher(); }, []);
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
